import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { PushQuoteService } from '@zero/shared/ui-push';
import { sortFilterCategories } from '@zero/shared/util-helper';
import { HistoricQuote, InstrumentType, Quote } from '@zero/shared/util-models';
import { isEmpty } from 'lodash-es';
import { catchError, distinctUntilChanged, filter, firstValueFrom, map, of, switchMap, tap } from 'rxjs';
import { StoreErrorHandler } from '../base/effects.base';
import { watchlistActions, watchlistApiActions } from './watchlist.actions';
import { WatchlistData, WatchlistItem, WatchlistVM } from './watchlist.entity';
import { WatchlistInfrastructure } from './watchlist.infrastrcuture';
import { watchlistFeature } from './watchlist.reducers';
import { selectWatchlistItemsByIsin } from './watchlist.selectors';

@Injectable()
export class WatchlistEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly watchlistInfrastructure = inject(WatchlistInfrastructure);
  private readonly pushQuotes = inject(PushQuoteService);
  private readonly storeErrorHandler = inject(StoreErrorHandler);

  public loadWatchlist$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(watchlistActions.loadWatchlist),
      concatLatestFrom(() => this.store.select(watchlistFeature.selectInitialLoaded)),
      filter(([{ force }, loaded]) => !loaded || force),
      // eslint-disable-next-line @ngrx/no-dispatch-in-effects
      tap(() => this.store.dispatch(watchlistActions.setLoading({ loading: true }))),
      switchMap(() =>
        this.watchlistInfrastructure.getWatchlist().pipe(
          map((watchlist) => watchlist.map(mapWatchlistDataToWatchlistVM)),
          map((watchlist) => watchlist.map(calculateWatchlistItemInitialPerformance)),
          map((watchlist) => this.resolveWlOrderIds(watchlist)),
          map((watchlist) => watchlistApiActions.loadWatchlistSuccess({ watchlist })),
          catchError((error) => of(watchlistApiActions.loadWatchlistFailure({ error }))),
        ),
      ),
      catchError((error) => of(watchlistApiActions.loadWatchlistFailure({ error }))),
    );
  });

  public addWatchlistItem$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(watchlistActions.addWatchlistItem),
      switchMap(({ isin }) =>
        this.watchlistInfrastructure.postWatchlistItem(isin).pipe(
          map((watchlist) => mapWatchlistDataToWatchlistVM(watchlist)),
          map((watchlist) => calculateWatchlistItemInitialPerformance(watchlist)),
          map((item) => watchlistApiActions.addWatchlistItemSuccess({ item })),
          catchError((error) => of(watchlistApiActions.addWatchlistItemFailure({ error }))),
        ),
      ),
      catchError((error) => of(watchlistApiActions.addWatchlistItemFailure({ error }))),
    );
  });

  public removeWatchlistItem$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(watchlistActions.removeWatchlistItem),
      switchMap(({ isin }) =>
        this.watchlistInfrastructure.deleteWatchlistItem(isin).pipe(
          map(() => isin),
          map((isin) => watchlistApiActions.removeWatchlistItemSuccess({ isin })),
          catchError((error) => of(watchlistApiActions.removeWatchlistItemFailure({ error }))),
        ),
      ),
      catchError((error) => of(watchlistApiActions.removeWatchlistItemFailure({ error }))),
    );
  });

  public saveWatchlistItem$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(watchlistActions.saveWatchlistItem, watchlistActions.updateWatchlistItemNote, watchlistActions.updateWatchlistItemEntryQuote),
      concatLatestFrom(() => this.store.select(watchlistFeature.selectEntities)),
      map(([{ isin }, entities]) => entities[isin]),
      map((item) => mapWatchlistVMToWatchlistItem(item)),
      switchMap((item) =>
        this.watchlistInfrastructure.putWatchlistItem(item).pipe(
          map(() => watchlistApiActions.saveWatchlistItemSuccess()),
          catchError((error) => of(watchlistApiActions.saveWatchlistItemFailure({ error }))),
        ),
      ),
      catchError((error) => of(watchlistApiActions.saveWatchlistItemFailure({ error }))),
    );
  });

  public saveWatchlist$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(watchlistActions.saveWatchlist),
      concatLatestFrom(() =>
        this.store.select(watchlistFeature.selectAll).pipe(map((watchlist) => watchlist.map(mapWatchlistVMToWatchlistItem))),
      ),
      switchMap(([, watchlist]) =>
        this.watchlistInfrastructure.putWatchlist(watchlist).pipe(
          map(() => watchlistApiActions.saveWatchlistSuccess()),
          catchError((error) => of(watchlistApiActions.saveWatchlistFailure({ error }))),
        ),
      ),
      catchError((error) => of(watchlistApiActions.saveWatchlistItemFailure({ error }))),
    );
  });

  public subscribeToQuotes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(watchlistActions.subscribeToWatchlistQuotes),
      switchMap(({ basedOnEntry }) => this.store.select(watchlistFeature.selectIds).pipe(map((isins) => ({ isins, basedOnEntry })))),
      distinctUntilChanged(),
      filter(({ isins }) => isins.length > 0),
      switchMap(({ basedOnEntry, isins }) =>
        this.pushQuotes.subscribeMultiBuffered(isins as string[]).pipe(
          map((quotes) => ({ quotes, basedOnEntry })),
          filter(({ quotes }) => !isEmpty(quotes)),
          concatLatestFrom(({ quotes }) => this.store.select(selectWatchlistItemsByIsin(Object.keys(quotes)))),
          map(([{ quotes, basedOnEntry }, watchlist]) =>
            watchlist.map((item) => mapQuoteToWatchlistVM(quotes, item, basedOnEntry)).filter((pos) => pos),
          ),
          map((watchlist) => watchlistActions.watchlistPerformanceCalculated({ watchlist })),
          catchError(() => of(watchlistActions.subscribeToWatchlistQuotesFailure())),
        ),
      ),
      catchError(() => of(watchlistActions.subscribeToWatchlistQuotesFailure())),
    );
  });

  public unsubscribeFromQuotes$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(watchlistActions.unsubscribeFromWatchlistQuotes),
        concatLatestFrom(() => this.store.select(watchlistFeature.selectIds)),
        tap(([, isins]) => this.pushQuotes.unsubscribeMulti(isins as string[])),
      );
    },
    { dispatch: false },
  );

  public subscribeToQuote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(watchlistActions.subscribeToWatchlistItemQuote),
      switchMap(({ basedOnEntry, isin }) =>
        this.pushQuotes.subscribeMultiBuffered([isin]).pipe(
          map((quotes) => ({ quotes, basedOnEntry })),
          filter(({ quotes }) => !isEmpty(quotes)),
          concatLatestFrom(({ quotes }) => this.store.select(selectWatchlistItemsByIsin(Object.keys(quotes)))),
          map(([{ quotes, basedOnEntry }, watchlist]) =>
            watchlist.map((item) => mapQuoteToWatchlistVM(quotes, item, basedOnEntry)).filter((pos) => pos),
          ),
          map((watchlist) => watchlistActions.watchlistPerformanceCalculated({ watchlist })),
          catchError(() => of(watchlistActions.subscribeToWatchlistQuotesFailure())),
        ),
      ),
      catchError(() => of(watchlistActions.subscribeToWatchlistQuotesFailure())),
    );
  });

  public unsubscribeFromQuote$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(watchlistActions.unsubscribeFromWatchlistItemQuote),
        tap(({ isin }) => this.pushQuotes.unsubscribe(isin)),
      );
    },
    { dispatch: false },
  );

  public setCategories$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        watchlistApiActions.loadWatchlistSuccess,
        watchlistApiActions.addWatchlistItemSuccess,
        watchlistApiActions.removeWatchlistItemSuccess,
      ),
      concatLatestFrom(() => this.store.select(watchlistFeature.selectAll)),
      map(([, watchlist]) => watchlist),
      map((watchlist) => {
        const positionsByType = watchlist.reduce<Record<string, (WatchlistVM | string)[]>>((acc, position) => {
          if (position.type && position.type !== InstrumentType.Crypto) {
            acc[position.type] = [...(acc[position.type] || []), position];
          }
          return acc;
        }, {});
        const categories = Object.keys(positionsByType);
        return categories;
      }),
      map((categories) => categories.sort(sortFilterCategories)),
      map((categories) => watchlistActions.setWatchlistCategories({ categories })),
    );
  });

  public handleBackendErrors$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          watchlistApiActions.loadWatchlistFailure,
          watchlistApiActions.addWatchlistItemFailure,
          watchlistApiActions.removeWatchlistItemFailure,
          watchlistApiActions.saveWatchlistFailure,
          watchlistApiActions.saveWatchlistItemFailure,
        ),
        tap((action) => {
          this.storeErrorHandler.handleBackendError(action.error);
        }),
      );
    },
    { dispatch: false },
  );

  private resolveWlOrderIds = (watchlist: WatchlistVM[]): WatchlistVM[] => {
    try {
      const orderIds = new Array<number>(watchlist.length);
      for (let i = 0; i < watchlist.length; i++) {
        orderIds[i] = watchlist[i].wlOrder;
      }
      orderIds.sort((a, b) => a - b);
      let hasDuplicates = false;
      let hasGaps = false;
      for (let i = 0; i < orderIds.length - 1; i++) {
        if (orderIds[i] === orderIds[i + 1]) {
          hasDuplicates = true;
          break;
        } else if (orderIds[i + 1] !== orderIds[i] + 1 && orderIds[i + 1] !== undefined) {
          hasGaps = true;
          break;
        }
      }

      if (hasDuplicates || hasGaps) {
        // re-order and save
        for (let i = 0; i < watchlist.length; i++) {
          watchlist[i].wlOrder = i;
        }
        firstValueFrom(this.watchlistInfrastructure.putWatchlist(watchlist.map(mapWatchlistVMToWatchlistItem)));
      }
      return watchlist;
    } catch (e) {
      return watchlist;
    }
  };
}

export const mapWatchlistDataToWatchlistVM = (item: WatchlistData): WatchlistVM => {
  const { additionalWatchlistData, instrument, watchlistItem } = item;
  return {
    ...instrument,
    ...watchlistItem,
    ...additionalWatchlistData,
    isSoldOut: isDerivativeSoldOut(item),
    isEndfaellig: instrument?.endfaellig,
    isKnockedOut: instrument?.knockedOutFlag,
    isSavingsPlanEligible: instrument?.sparplan,
  } as WatchlistVM;
};

export const mapWatchlistVMToWatchlistItem = (item: WatchlistVM): WatchlistItem => {
  const {
    id,
    isin,
    wlOrder,
    added,
    askWhenAdded,
    bidWhenAdded,
    ask,
    bid,
    quoteTime,
    itemName,
    note,
    cachedHistoricQuote,
    newestHistoricQuote,
  } = item;

  return {
    id,
    isin,
    wlOrder,
    added,
    askWhenAdded,
    bidWhenAdded,
    ask,
    bid,
    quoteTime,
    itemName,
    note,
    cachedHistoricQuote,
    newestHistoricQuote,
  } as WatchlistItem;
};

const calculateWatchlistItemInitialPerformance = (entry: WatchlistVM): WatchlistVM => {
  if (Array.isArray(entry.historicQuotes) && entry.historicQuotes.length > 0) {
    const cachedHistoricQuote = entry.historicQuotes.slice().sort((a, b) => (a.ts > b.ts ? -1 : 1))[0];
    const { bid } = cachedHistoricQuote;

    const historicBid = bid;
    const newBid = entry.bid;

    return {
      ...entry,
      dayBeforeAbs: historicBid !== 0 ? newBid - historicBid : 0,
      dayBeforePct: historicBid !== 0 ? ((newBid - historicBid) / historicBid) * 100 : 0,
      perfAbs: newBid - entry.bidWhenAdded,
      perfPct: ((newBid - entry.bidWhenAdded) / entry.bidWhenAdded) * 100,
      cachedHistoricQuote: cachedHistoricQuote,
    };
  }

  return entry;
};

const mapQuoteToWatchlistVM = (quotes: Record<string, Quote>, entry: WatchlistVM, basedOnEntry: boolean) => {
  if (!entry && !entry.isin) {
    return null;
  }

  const rawQuote = quotes[entry.isin];

  if (!rawQuote) {
    return null;
  }

  let cachedHistoricQuote = isQuoteNew(entry, rawQuote) ? null : entry.cachedHistoricQuote;
  const quoteTime = rawQuote.time;

  const ask = rawQuote.ask;
  const bid = rawQuote.bid;
  const perfAbs = rawQuote.bid - entry.bidWhenAdded;
  const perfPct = ((rawQuote.bid - entry.bidWhenAdded) / entry.bidWhenAdded) * 100;

  let newestHistoricQuote: HistoricQuote;
  if (!entry.cachedHistoricQuote) {
    let ret: HistoricQuote = new HistoricQuote(0, undefined, 0, 0);
    if (entry?.historicQuotes && entry.historicQuotes.length > 0) {
      const historicQuotes = entry.historicQuotes.slice().sort((a, b) => (a.ts > b.ts ? -1 : 1));
      newestHistoricQuote = historicQuotes[0];
      ret = newestHistoricQuote;
    }

    cachedHistoricQuote = ret;
  }

  const historicBid = entry.cachedHistoricQuote?.bid ?? 0;
  const dayBeforeAbs = historicBid !== 0 ? rawQuote.bid - historicBid : 0;
  const dayBeforePct = historicBid !== 0 ? (dayBeforeAbs / historicBid) * 100 : 0;
  const calcBase = basedOnEntry ? entry.bidWhenAdded : historicBid;
  const customEntryQuote = entry.bidWhenAdded;
  const prevDayQuote = historicBid;

  return {
    ...entry,
    ask,
    bid,
    quoteTime,
    perfAbs,
    perfPct,
    cachedHistoricQuote,
    newestHistoricQuote,
    dayBeforeAbs,
    dayBeforePct,
    calcBase,
    customEntryQuote,
    prevDayQuote,
  };
};

const isDerivativeSoldOut = (item: WatchlistData): boolean => {
  return (
    Boolean(item.instrument) &&
    item.instrument.type === InstrumentType.Derivative &&
    !item.instrument.knockedOutFlag &&
    Boolean(item.watchlistItem.bid) &&
    item.watchlistItem.ask === 0 &&
    item.watchlistItem.bid > 0
  );
};

const isQuoteNew = (entry: WatchlistVM, quote: Quote) => {
  return (
    entry.quoteTime &&
    quote.time > entry.quoteTime &&
    entry.cachedHistoricQuote &&
    entry.newestHistoricQuote &&
    entry.newestHistoricQuote.ts > entry.cachedHistoricQuote.ts &&
    quote.time > entry.cachedHistoricQuote.ts
  );
};
